import { useState } from 'react';
import { Container, Title, Button, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import {
  useCustomFieldDefinitions,
  useCreateCustomFieldDefinition,
  useUpdateCustomFieldDefinition,
  useDeleteCustomFieldDefinition,
  useAssignCategoriesToCustomField,
  type CustomFieldDefinition,
} from '@/hooks/api/useCustomFields';
import { CustomFieldsTable } from './components/CustomFieldsTable';
import { CustomFieldForm } from './components/CustomFieldForm';
import type { CustomFieldFormValues } from './schemas/customFieldSchema';

export function CustomFieldsPage() {
  const { organizationId } = useOrganization();
  const { data: fields = [], isLoading } = useCustomFieldDefinitions(organizationId);
  const createField = useCreateCustomFieldDefinition();
  const updateField = useUpdateCustomFieldDefinition();
  const deleteField = useDeleteCustomFieldDefinition();
  const assignCategories = useAssignCategoriesToCustomField();

  const [formOpened, setFormOpened] = useState(false);
  const [selectedField, setSelectedField] = useState<CustomFieldDefinition | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const nextOrder = fields.length > 0 ? Math.max(...fields.map((f) => f.display_order)) + 1 : 0;

  const handleCreate = () => {
    setSelectedField(null);
    setIsEditing(false);
    setFormOpened(true);
  };

  const handleEdit = (field: CustomFieldDefinition) => {
    setSelectedField(field);
    setIsEditing(true);
    setFormOpened(true);
  };

  const handleDelete = (field: CustomFieldDefinition) => {
    modals.openConfirmModal({
      title: 'Delete Custom Field',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{field.name}</strong>? This will also delete all
          values for this field. This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteField.mutate(field.id);
      },
    });
  };

  const handleSubmit = async (values: CustomFieldFormValues, keepOpen = false) => {
    if (!organizationId) return;

    // Parse options string into JSON array for dropdown fields
    const options =
      values.field_type === 'dropdown' && values.options
        ? values.options
            .split('\n')
            .map((opt) => opt.trim())
            .filter((opt) => opt.length > 0)
        : null;

    const fieldData = {
      name: values.name,
      field_type: values.field_type,
      options,
      is_required: values.is_required,
      default_value: values.default_value || null,
      display_order: values.display_order,
      is_active: values.is_active,
    };

    // Determine category IDs - empty array if not limiting to categories
    const categoryIds = values.limit_to_categories && values.category_ids?.length
      ? values.category_ids
      : [];

    if (isEditing && selectedField) {
      await updateField.mutateAsync({
        id: selectedField.id,
        data: fieldData,
      });
      // Update category assignments
      await assignCategories.mutateAsync({
        fieldId: selectedField.id,
        categoryIds,
      });
    } else {
      const newField = await createField.mutateAsync({
        ...fieldData,
        organization_id: organizationId,
      });
      // Assign categories to new field
      if (categoryIds.length > 0 && newField?.id) {
        await assignCategories.mutateAsync({
          fieldId: newField.id,
          categoryIds,
        });
      }
    }

    if (!keepOpen) {
      setFormOpened(false);
      setSelectedField(null);
    }
  };

  return (
    <Container fluid>
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={1}>Custom Fields</Title>
            <Text c="dimmed" size="sm">
              Add custom fields to capture additional asset information
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            Add Custom Field
          </Button>
        </Group>

        <CustomFieldsTable
          fields={fields}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
        />

        <CustomFieldForm
          opened={formOpened}
          onClose={() => {
            setFormOpened(false);
            setSelectedField(null);
          }}
          onSubmit={handleSubmit}
          field={isEditing ? selectedField : null}
          isLoading={createField.isPending || updateField.isPending}
          nextOrder={nextOrder}
        />
      </Stack>
    </Container>
  );
}
